import React from 'react';
import Dropdown from '../Common/Dropdown';
import DateInput from '../Common/DateInput';
import RadioInput from '../Common/RadioInput';
import '@aurodesignsystem/auro-button';

interface FormValues {
  flightRangeStart: string;
  flightRangeEnd: string;
  operatingCarrier: string;
  flightType: string;
  effectiveDate: string;
  wetLease: string;
}

interface FlightRangeFormProps {
  values: FormValues;
  onChange: {
    setFlightRangeStart: (value: string) => void;
    setFlightRangeEnd: (value: string) => void;
    setOperatingCarrier: (value: string) => void;
    setFlightType: (value: string) => void;
    setEffectiveDate: (value: string) => void;
    setWetLease: (value: string) => void;
  };
  flightTypeOptions: { value: string; label: string; title: string }[];
  carrierOptions: { value: string; label: string }[];
  isLoadingFlightTypes: boolean;
  isLoadingCarriers?: boolean;
  onUpdate: () => void;
  onReset: () => void;
}

const FlightRangeForm: React.FC<FlightRangeFormProps> = ({
  values,
  onChange,
  flightTypeOptions,
  carrierOptions,
  isLoadingFlightTypes,
  isLoadingCarriers = false,
  onUpdate,
  onReset
}) => {
  const handleWetLeaseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange.setWetLease(e.target.value);
  };

  // Render carrier dropdown or loading/error states
  const renderCarrierDropdown = () => {
    if (isLoadingCarriers) {
      return <div className="flight-range-modify__loading" aria-live="polite">Loading carriers...</div>;
    }

    if (carrierOptions.length === 0) {
      return (
        <div className="flight-range-modify__loading flight-range-modify__loading--error" aria-live="polite">
          No carriers available. Please try again.
        </div>
      );
    }

    return (
      <Dropdown
        id="operating-carrier"
        value={values.operatingCarrier}
        options={carrierOptions}
        onChange={onChange.setOperatingCarrier}
        width="100%"
        className="flight-range-modify__carrier-dropdown"
        aria-label="Select operating carrier"
      />
    );
  };

  // Render flight type dropdown or loading/error states
  const renderFlightTypeDropdown = () => {
    if (isLoadingFlightTypes) {
      return <div className="flight-range-modify__loading" aria-live="polite">Loading flight types...</div>;
    }

    if (flightTypeOptions.length === 0) {
      return (
        <div className="flight-range-modify__loading flight-range-modify__loading--error" aria-live="polite">
          No flight types available. Please try again.
        </div>
      );
    }

    return (
      <Dropdown
        id="flight-type"
        value={values.flightType}
        options={flightTypeOptions.slice().sort((a, b) => a.label.localeCompare(b.label))}
        onChange={onChange.setFlightType}
        width="100%"
        className="flight-range-modify__flight-type-dropdown"
        aria-label="Select flight type"
      />
    );
  };

  return (
    <div className="flight-range-modify__form">
      <div className="flight-range-modify__form-grid">
        <div className="flight-range-modify__field-column">
          <label className="flight-range-modify__label" htmlFor="flight-range-start">Flight range</label>
          <div className="flight-range-modify__range-inputs">
            <input
              id="flight-range-start"
              type="text"
              value={values.flightRangeStart}
              onChange={(e) => onChange.setFlightRangeStart(e.target.value)}
              className="flight-range-modify__range-input"
              aria-label="Flight range start"
            />
            <span className="flight-range-modify__range-separator">to</span>
            <input
              id="flight-range-end"
              type="text"
              value={values.flightRangeEnd}
              onChange={(e) => onChange.setFlightRangeEnd(e.target.value)}
              className="flight-range-modify__range-input"
              aria-label="Flight range end"
            />
          </div>
        </div>

        <div className="flight-range-modify__divider"></div>
        
        <div className="flight-range-modify__field-column">
          <label className="flight-range-modify__label" htmlFor="operating-carrier">Operating carrier</label>
          {renderCarrierDropdown()}
        </div>

        <div className="flight-range-modify__field-column">
          <label className="flight-range-modify__label" htmlFor="flight-type">Flight type</label>
          {renderFlightTypeDropdown()}
        </div>

        <div className="flight-range-modify__field-column">
          <label className="flight-range-modify__label" htmlFor="effective-date">Effective date</label>
          <DateInput
            id="effective-date"
            value={values.effectiveDate}
            onChange={onChange.setEffectiveDate}
            width="100%"
            className="flight-range-modify__date-input"
            aria-label="Select effective date"
          />
        </div>
        
        <div className="flight-range-modify__divider"></div>
        
        <div className="flight-range-modify__field-column">
          <fieldset>
            <legend className="flight-range-modify__label">Is this a wet lease?</legend>
            <div className="flight-range-modify__radio-group">
              <RadioInput
                id="wet-lease-yes"
                name="wetLease"
                value="yes"
                label="Yes"
                checked={values.wetLease === 'yes'}
                onChange={handleWetLeaseChange}
              />
              <RadioInput
                id="wet-lease-no"
                name="wetLease"
                value="no"
                label="No"
                checked={values.wetLease === 'no'}
                onChange={handleWetLeaseChange}
              />
            </div>
          </fieldset>
        </div>
        
        <div className="flight-range-modify__form-actions">
          <auro-button
            className="flight-range-modify__update-button"
            onClick={onUpdate}
            aria-label="Update flight range"
            variant='primary'
            size='sm'
            style={
              {
                '--ds-auro-button-container-image': 'var(--color-primary, #0074ca)',
              } as React.CSSProperties
            }
          >
            Update
          </auro-button>
          <button
            className="flight-range-modify__reset-link"
            onClick={onReset}
            type="button"
            aria-label="Reset form"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlightRangeForm;
